import React, {useEffect, useState} from "react";
import type { ContextProps } from '../../../../types';
import type { BraillerLoanManagementItem, BraillerLoanManagementUpdateItem, BraillerLoanFilterLists, BraillerCatalogItemSummary, BraillerSerialNumberSwapValidation, BraillerSerialNumberSwapResult, BraillerLoanManagementApiItem } from '../brailler.types';
import {isNullOrUndefined} from "../../../../Utilities/commonUtilities";
import {braillerLoanApi} from "../BraillerLoan/braillerLoanApi";
import {braillerCatalogApi} from "../BraillerCatalog/braillerCatalogApi";
import {PageTitle} from "../../../../Components/Landmarks";
import {BraillerLoanManagementSearchFormView} from "./BraillerLoanManagementSearchFormView";
import {LayoutFullWidth} from "../../../../Components/Layout";
import {BraillerLoanManagementTable} from "./BraillerLoanManagementTable";
import {allow, policyEvents} from "../../../../Components/Authorize";
import {braillerLoanManagementItemModel, updateBraillerLoanManagementItemModel} from "./braillerLoanManagementFactory";
import {GridCell, gridConstants, GridContainer} from "../../../../Components/Grid";
import {Button, ButtonLinkStyle} from "../../../../Components/Inputs";
import {getChangedLoanManagementItemsForUpdate} from "./braillerLoanManagementUtility";
import {isArrayNullOrEmpty} from "../../../../Utilities/Types/arrayUtilities";
import {braillerRepairApi} from "../../../../SiteConfig/braillerRepairApi";
import {openInNewTab} from "../../../../Utilities/htmlUtilities";
import {locations} from "../../../../Utilities/Location";

export const BraillerLoanManagementContainer = ({
                                                    handleApiCall,
                                                    handleSetBraillerLoanManagementSearchCriteria,
                                                    isLoading,
                                                    braillerLoanManagementSearchCriteria
                                                }: ContextProps) => {

    const [managementItems, setManagementItems] = useState<BraillerLoanManagementItem[]>([]);
    const [originalManagementItems, setOriginalManagementItems] = useState<BraillerLoanManagementItem[]>([]);
    const [searchDropDowns, setSearchDropDowns] = useState<BraillerLoanFilterLists | null>(null);
    const [catalogItems, setCatalogItems] = useState<BraillerCatalogItemSummary[]>([]);
    const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);

    // Use existing permission - check available permissions
    const canManageBraillerLoans = allow(policyEvents.MANAGE_BRAILLER_CATALOG);

    const loadManagementItemsResult = (result: { items?: BraillerLoanManagementApiItem[]; totalCount?: number }) => {
        const items = (result?.items || []).map((item: BraillerLoanManagementApiItem) => braillerLoanManagementItemModel(item));
        setManagementItems(items);
        setOriginalManagementItems(items);
        setTotalRecords(result?.totalCount || 0);
    };

    const handleGetManagementItems = () => {
        handleApiCall(() => braillerLoanApi.getLoansAndRepairsForManagement({
            ...braillerLoanManagementSearchCriteria,
        }), loadManagementItemsResult);
    };

    const handleGetSearchDropDowns = () => {
        handleApiCall(() => braillerLoanApi.getDropdownListsForBraillerLoanSearch(), (results: BraillerLoanFilterLists) => {
            setSearchDropDowns(results)
        });
    };

    const handleGetCatalogItems = () => {
        setIsLoadingCatalog(true);
        setCatalogItems([]);
        handleApiCall(
            () => braillerCatalogApi.getAllBraillerCatalogItems(),
            (result: BraillerCatalogItemSummary[]) => {
                setCatalogItems(result || []);
                setIsLoadingCatalog(false);
            },
            () => {
                setIsLoadingCatalog(false);
            }
        );
    };

    const handleCreateReplacementLoan = (repairId: string, serialNumber: string, catalogItemId: string, shelfLocation: string, onSuccess?: (result: unknown) => void) => {
        handleApiCall(
            () => braillerRepairApi.createReplacementLoan(repairId, serialNumber, catalogItemId, shelfLocation),
            (result: unknown) => {
                // Refresh the management items to show updated status
                handleGetManagementItems();
                if (onSuccess) {
                    onSuccess(result);
                }
            }
        );
    };

    const handleCancelLoan = (loanId: string, notes: string | null) => {
        handleApiCall(
            () => braillerLoanApi.cancelLoan(loanId, notes),
            () => {
                handleGetManagementItems();
            }
        );
    };

    // Serial Number Swap - Validate
    const handleValidateSerialNumberSwap = (loanId: string, newSerialNumber: string, onValidationComplete: (result: BraillerSerialNumberSwapValidation) => void) => {
        handleApiCall(
            () => braillerLoanApi.validateSerialNumberSwap(loanId, newSerialNumber),
            (result: BraillerSerialNumberSwapValidation) => {
                onValidationComplete(result);
            }
        );
    };

    // Serial Number Swap - Execute
    const handleExecuteSerialNumberSwap = (loanId: string, newSerialNumber: string, onComplete: (result: BraillerSerialNumberSwapResult) => void) => {
        const swapRequest = {
            loanId,
            newSerialNumber,
            confirmReturn: true,
            confirmCreateInventory: true,
            confirmUnavailableOverride: true,
            confirmCatalogMismatch: true
        };

        handleApiCall(
            () => braillerLoanApi.executeSerialNumberSwap(loanId, swapRequest),
            (result: BraillerSerialNumberSwapResult) => {
                if (result.success) {
                    handleGetManagementItems();
                }
                onComplete(result);
            }
        );
    };

    const updateManagementItemInState = (id: string, type: string, status: number | null, notes: string | null, serialNumber?: string | null, issue?: string | null, inventoryNotes?: string | null, resolution?: string | null, repairStatus?: number | null) => {
        setManagementItems((currentItems) =>
            currentItems.map((item: BraillerLoanManagementItem) =>
                item.id === id && item.type === type
                    ? {
                        ...item,
                        ...(type === 'Loan' ? {loanStatus: status} : {repairStatus: status}),
                        notes: notes ? notes : item.notes,
                        inventoryNotes: inventoryNotes !== undefined ? inventoryNotes : item.inventoryNotes,
                        ...(serialNumber ? {serialNumber} : {}),
                        ...(type === 'Repair' && notes ? {resolution: notes} : {}),
                        ...(type === 'Repair' && issue ? {issue} : {}),
                        ...(type === 'Loan' && issue ? {issue} : {}),
                        ...(type === 'Loan' && resolution ? {resolution} : {}),
                        ...(type === 'Loan' && repairStatus !== undefined ? {repairStatus} : {})
                    }
                    : item
            )
        );
    };

    const syncOriginalManagementItemAfterSave = (apiResponse: Record<string, unknown> | Record<string, unknown>[]) => {
        const raw = Array.isArray(apiResponse) ? apiResponse[0] : apiResponse;
        const updatedFromApi = raw as Record<string, string | number | boolean | null | undefined | Record<string, unknown>>;

        const mappedUpdate = updatedFromApi.type === 'Repair' || updatedFromApi.issue !== undefined
            ? {
                id: updatedFromApi.id as string,
                type: 'Repair' as const,
                issue: (updatedFromApi.issue as string | null),
                resolution: (updatedFromApi.resolution as string | null),
                serialNumber: (updatedFromApi.serialNumber as string | null) || (updatedFromApi.braillerInventoryItem as Record<string, unknown> | undefined)?.serialNumber as string | null,
                repairStatus: (updatedFromApi.status as number | null),
                notes: (updatedFromApi.resolution as string | null)
            }
            : {
                id: updatedFromApi.braillerLoanId as string,
                type: 'Loan' as const,
                loanStatus: (updatedFromApi.braillerLoanStatus as number | null),
                notes: (updatedFromApi.braillerLoanNotes as string | null),
                serialNumber: (updatedFromApi.serialNumber as string | null) || (updatedFromApi.inventoryBarCode as string | null)
            };

        setManagementItems((currentItems) =>
            currentItems.map((item: BraillerLoanManagementItem) =>
                item.id === mappedUpdate.id && item.type === mappedUpdate.type
                    ? {
                        ...item,
                        ...(mappedUpdate.type === 'Loan' ? {loanStatus: mappedUpdate.loanStatus} : {repairStatus: mappedUpdate.repairStatus}),
                        notes: mappedUpdate.notes || item.notes,
                        ...(mappedUpdate.serialNumber ? {serialNumber: mappedUpdate.serialNumber} : {}),
                        ...(mappedUpdate.type === 'Repair' && mappedUpdate.resolution ? {resolution: mappedUpdate.resolution} : {}),
                        ...(mappedUpdate.type === 'Repair' && mappedUpdate.issue ? {issue: mappedUpdate.issue} : {})
                    } as BraillerLoanManagementItem
                    : item
            )
        );

        setOriginalManagementItems((currentItems) =>
            currentItems.map((item: BraillerLoanManagementItem) =>
                item.id === mappedUpdate.id && item.type === mappedUpdate.type
                    ? {
                        ...item,
                        ...(mappedUpdate.type === 'Loan' ? {loanStatus: mappedUpdate.loanStatus} : {repairStatus: mappedUpdate.repairStatus}),
                        notes: mappedUpdate.notes || item.notes,
                        ...(mappedUpdate.serialNumber ? {serialNumber: mappedUpdate.serialNumber} : {}),
                        ...(mappedUpdate.type === 'Repair' && mappedUpdate.resolution ? {resolution: mappedUpdate.resolution} : {}),
                        ...(mappedUpdate.type === 'Repair' && mappedUpdate.issue ? {issue: mappedUpdate.issue} : {})
                    } as BraillerLoanManagementItem
                    : item
            )
        );
    };

    const handleSaveUpdatedManagementItem = (updatedItem: BraillerLoanManagementItem) => {
        updateManagementItemInState(
            updatedItem.id,
            updatedItem.type,
            updatedItem.type === 'Loan' ? updatedItem.loanStatus : updatedItem.repairStatus,
            updatedItem.notes,
            updatedItem.serialNumber,
            updatedItem.issue,
            updatedItem.inventoryNotes,
            updatedItem.resolution,
            updatedItem.type === 'Loan' ? updatedItem.repairStatus : undefined
        );

        if (updatedItem.type === 'Loan' && updatedItem.braillerRepairId) {
            const loanUpdate = {
                braillerLoanId: updatedItem.braillerLoanId,
                braillerLoanStatus: updatedItem.loanStatus,
                braillerLoanNotes: updatedItem.notes,
                inventoryNotes: updatedItem.inventoryNotes,
                serialNumber: updatedItem.serialNumber
            };

            const repairUpdate = {
                id: updatedItem.braillerRepairId,
                issue: updatedItem.issue,
                resolution: updatedItem.resolution,
                status: updatedItem.repairStatus,
                inventoryNotes: updatedItem.inventoryNotes,
                serialNumber: updatedItem.serialNumber,
                repairedBy: updatedItem.repairedBy || ''
            };

            handleApiCall(() => braillerLoanApi.updateLoans([loanUpdate]), () => {
                handleApiCall(() => braillerRepairApi.updateRepairs([repairUpdate]), syncOriginalManagementItemAfterSave);
            });
        } else {
            const itemForSaving = [updateBraillerLoanManagementItemModel(updatedItem)];

            if (updatedItem.type === 'Loan') {
                handleApiCall(() => braillerLoanApi.updateLoans(itemForSaving), syncOriginalManagementItemAfterSave);
            } else {
                handleApiCall(() => braillerRepairApi.updateRepairs(itemForSaving), syncOriginalManagementItemAfterSave);
            }
        }
    };

    const handleSaveUpdatedManagementItems = () => {
        const updatedItems = getChangedLoanManagementItemsForUpdate(managementItems, originalManagementItems);

        if (updatedItems.length === 0) {
            alert("No changes to save.");
            return;
        }

        if (confirm(`Are you sure you want to save ${updatedItems.length} updated item(s)?`)) {
            // Filter by checking for the presence of braillerLoanId (loans) vs type === 'Repair'
            const loanUpdates = updatedItems.filter((item: BraillerLoanManagementUpdateItem) => item.braillerLoanId);
            const repairUpdates = updatedItems.filter((item: BraillerLoanManagementUpdateItem) => !item.braillerLoanId);

            const savePromises = [];

            if (loanUpdates.length > 0) {
                savePromises.push(
                    new Promise((resolve) => {
                        handleApiCall(() => braillerLoanApi.updateLoans(loanUpdates), resolve);
                    })
                );
            }

            if (repairUpdates.length > 0) {
                savePromises.push(
                    new Promise((resolve) => {
                        handleApiCall(() => braillerRepairApi.updateRepairs(repairUpdates), resolve);
                    })
                );
            }

            if (savePromises.length > 0) {
                Promise.all(savePromises).then(() => {
                    handleGetManagementItems();
                });
            }
        }
    };

    const handleExportItemsClick = () => {
        if (isArrayNullOrEmpty(managementItems)) {
            alert("Search criteria has no results. Adjust your search and try again.");
        }
        else 
            handleApiCall(() => braillerLoanApi.getBraillerLoansReport(braillerLoanManagementSearchCriteria));
    };

    const handleBraillerPackingList = (braillerLoanId: string) => {
        openInNewTab(locations.BRAILLER_PACKING_LIST.getLink(braillerLoanId).to);
    };

    useEffect(() => {
        handleGetManagementItems();
    }, [braillerLoanManagementSearchCriteria]);

    useEffect(() => {
        handleGetSearchDropDowns();
        handleGetCatalogItems();
    }, []);

    if (isNullOrUndefined(searchDropDowns)) return null;

    return (
        <>
            <PageTitle h1="Brailler Loan Management"/>

            <div className={`searchBox`}>

                <div className={`searchBox__title`}>
                    <span>Search for Brailler Loans</span>
                </div>

                <BraillerLoanManagementSearchFormView
                    searchCriteria={braillerLoanManagementSearchCriteria}
                    handleSetSearchCriteria={handleSetBraillerLoanManagementSearchCriteria}
                    isLoading={isLoading}
                    searchDropDowns={searchDropDowns}
                />

            </div>


            <LayoutFullWidth className="catalog">
                <header>
                    <h2>Loans and Repairs</h2>
                </header>
                {
                    managementItems && (
                        <>
                            <GridContainer isReverseOrder className="form__header" hasMarginX>
                                <GridCell
                                    medium_columns={gridConstants.column.THREE}
                                    small_columns={gridConstants.column.SIX}>
                                    <Button
                                        disabled={isLoading}
                                        isPrimary
                                        label="Save Updates"
                                        name="btnSave"
                                        onClick={handleSaveUpdatedManagementItems}
                                    />
                                </GridCell>
                                <GridCell
                                    medium_columns={gridConstants.column.ONE}>
                                    <ButtonLinkStyle
                                        isCollapsed
                                        label="Export Items"
                                        name="btnExportItems"
                                        onClick={handleExportItemsClick}
                                    />
                                </GridCell>
                            </GridContainer>

                            <BraillerLoanManagementTable
                                managementItems={managementItems}
                                searchCriteria={braillerLoanManagementSearchCriteria}
                                canManageBraillerLoans={canManageBraillerLoans}
                                handleSetSearchCriteria={handleSetBraillerLoanManagementSearchCriteria}
                                handleSaveUpdatedManagementItem={handleSaveUpdatedManagementItem}
                                updateManagementItemInState={updateManagementItemInState}
                                onCreateReplacementLoan={handleCreateReplacementLoan}
                                catalogItems={catalogItems}
                                isLoadingCatalog={isLoadingCatalog}
                                handleBraillerPackingList={handleBraillerPackingList}
                                onCancelLoan={handleCancelLoan}
                                onValidateSerialNumberSwap={handleValidateSerialNumberSwap}
                                onExecuteSerialNumberSwap={handleExecuteSerialNumberSwap}
                                totalRecords={totalRecords}
                            />
                        </>
                    )
                }
            </LayoutFullWidth>
        </>
    );
};

